import { useCallback, useState } from "react";
import { useShoppingCartApi } from "@/hooks/apiHooks/useShoppingCartApi";
import type { ProductVendor } from "@models/product";
import type { ShoppingCartItem } from "@models/shoppingCart";
import {
    cartRefusal,
    switchStockMessage,
    type LineRefusal,
} from "../../_helpers/stockCopy";

interface VendorSwitch {
    // Resolves true once the line was switched (and the cart re-read).
    switchVendor: (
        item: ShoppingCartItem,
        vendor: ProductVendor,
    ) => Promise<boolean>;
    // The line whose switch is in flight — through the cart re-read that follows it.
    switchingItemId: string | null;
    // Why a line's last switch was refused, by cart item id.
    refusals: Record<string, LineRefusal>;
    // Drops a line's refusal once the line is changed some other way (a quantity edit).
    clearRefusal: (itemId: string) => void;
}

/**
 * Moves a cart line to another vendor offering (#443 / C-07: a vendor with enough
 * stock, rather than a refusal). The API's stock refusal is kept per line for the row
 * to show; any other failure has been toasted by useApi, except a 403, which the cart
 * hook toasted itself or answered by moving the customer to a wall (#368).
 */
export const useVendorSwitch = (): VendorSwitch => {
    const { switchCartItemVendor, getShoppingCart } = useShoppingCartApi();
    const [switchingItemId, setSwitchingItemId] = useState<string | null>(null);
    const [refusals, setRefusals] = useState<Record<string, LineRefusal>>({});

    const clearRefusal = useCallback((itemId: string) => {
        setRefusals((prev) => {
            if (!(itemId in prev)) return prev;
            const next = { ...prev };
            delete next[itemId];
            return next;
        });
    }, []);

    const switchVendor = useCallback(
        async (item: ShoppingCartItem, vendor: ProductVendor) => {
            setSwitchingItemId(item.id);
            clearRefusal(item.id);

            try {
                await switchCartItemVendor(item.id, vendor.id);
            } catch (error) {
                // The switch's stock refusal says how many MORE the target can
                // take (`addable`, always sent on a switch; absent is read as none).
                const refusal = cartRefusal(error, ({ addable }) =>
                    switchStockMessage(vendor.vendorName, addable ?? 0),
                );
                if (refusal) {
                    setRefusals((prev) => ({ ...prev, [item.id]: refusal }));
                }
                setSwitchingItemId(null);
                return false;
            }

            // The line's vendor and price changed, and a switch can merge it into
            // another line — only the server's cart has the result.
            await getShoppingCart().catch(() => {
                /* useApi toasted it; the cart's error atom has it */
            });
            setSwitchingItemId(null);
            return true;
        },
        [switchCartItemVendor, getShoppingCart, clearRefusal],
    );

    return { switchVendor, switchingItemId, refusals, clearRefusal };
};
